import { Add, Close, FiberManualRecord } from "@mui/icons-material";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import PageHeader from "../../common/components/PageHeader";
import CommonButton from "../../common/components/CommonButton";
import { ProfilesObj, ProjectCategory, ProjectTypeObj, ProjecType, Status, UserTypeId } from "../../common/const";
import theme from "../../theme";
import DcpProject from "./DcpProject";
import ExternalProject from "./ExternalProject";
import ExploreProject from "./ExploreProject";
import CommonDialog from "../../common/components/CommonDialog";
import CommonFormField from "../../common/components/CommonFormField";
import CommonSelect from "../../common/components/CommonSelect";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { getProject, getProjectByStudent } from "../../api/projectApi";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { createProjectResearch } from "../../api/resourceApi";
import { useLocation, useNavigate } from "react-router-dom";
import { acceptRequest, rejectRequest } from "../../api/memberListApi";
import CommonCard from "../../common/components/CommonCard";

const BuildProject = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const { user, loading } = useSelector((state) => state.auth);
    const [projectType, setProjectType] = useState(ProjecType.DCP);
    const [addDialog, setAddDialog] = useState(false);
    const [successOpen, setSuccess] = useState(false);
    const [errors, setErrors] = useState({});
    const [projectList, setProjectList] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [projectData, setProjectData] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        category: [],
        status: Status[0].value,
        startdate: '',
        projectUrl: '',
        description: '',
    });


    const validateField = (name, value) => {
        switch (name) {
            case 'title':
                if (!value) return `Project Title is required`;
                return '';
            case 'startdate':
                if (!value) return `Start Date is required`;
                return '';
            case 'projectUrl':
                if (!value) return `Project Url is required`;
                return '';
            default:
                return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear error for the field being changed
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            if (formData.category.length > 0) {
                dispatch(setLoading({
                    loading: true
                }));
                const response = await createProjectResearch({
                    "project_profile": ProfilesObj.Project,
                    "categories": formData.category,
                    "project_name": formData.title,
                    "project_source": ProjectTypeObj.ext,
                    "is_it_first_time": true,
                    "status": formData.status,
                    "is_active": true,
                    "start_date": new Date(formData.startdate).toISOString(),
                    "student_id": user?.student_id,
                    project_url: formData?.projectUrl,
                    description: formData?.description
                });
                if (response.success) {
                    setSuccess(true);
                    setAddDialog(false);
                    dispatch(setLoading({
                        loading: false
                    }));
                    setFormData({
                        title: '',
                        category: [],
                        status: Status[0].value,
                        startdate: '',
                        projectUrl: '',
                        description: ''
                    });
                }
            } else {
                showNotification('Please select Domain/category', 'error');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            dispatch(setLoading({
                loading: false
            }));
            showNotification(errorMessage, 'error');
        }
    }

    const getProjects = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getProjectByStudent(user?.student_id, projectType === ProjecType.DCP ? ProjectTypeObj.dcp : ProjectTypeObj.ext);
            setProjectList(response?.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            dispatch(setLoading({
                loading: false
            }));
            showNotification(errorMessage, 'error');
        }
    };

    useEffect(() => {
        if ((projectType === ProjecType.DCP || projectType === ProjecType.EXTERNAL) && user?.student_id) {
            setProjectList([]);
            getProjects();
        }
    }, [projectType, user]);

    const getProjectDetails = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getProject(searchParams.get('project_id'));
            setProjectData(response?.data);
            const responseData = await fetch(`${process.env.REACT_APP_BASE_URL}/file?bname=${response?.data?.images?.url}&download=false`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const blob = await responseData.blob();
            const imageUrl = URL.createObjectURL(blob);
            setPreviewUrl(imageUrl || '');
            setDialogOpen(true);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    useEffect(() => {
        if (searchParams.get('project_id')) {
            dispatch(setLoading({
                loading: true
            }));
            getProjectDetails();
        }
    }, [searchParams.get('project_id')]);

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonDialog
                open={dialogOpen}
                size="xs"
                padding="0px"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Accept Invitation</Typography>
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Fragment>
                        <Box display="flex" gap={1} width="100%" flexDirection="column" mt={1} mb={1}>
                            <img
                                src={previewUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(`${projectData?.project_name}`)}&background=FAFAFAf1&color=fff`}
                                alt="project"
                                width="200px"
                                height="190px"
                                style={{ borderRadius: 10 }}
                            />
                            <Box>
                                <Typography color="secondary.main" fontSize={14}>{projectData?.project_source === ProjectTypeObj.dcp ? 'DCP Project' : 'External Project'}</Typography>
                                <Typography fontWeight={600}>{projectData?.project_name}</Typography>
                                <Typography fontSize={14} color="background.dark">{projectData?.description}</Typography>
                                <CommonCard
                                    paddingBottom="0px"
                                    padding="12px"
                                    borderRadius={2}
                                    marginTop={0.8}
                                    background={theme.palette.background.main}
                                    content={
                                        <Fragment>
                                            <Typography fontSize={14} fontWeight={600}>Goal</Typography>
                                            <Typography
                                                fontSize={14}
                                                color={theme.palette.background.dark}
                                                textAlign="justify"
                                                dangerouslySetInnerHTML={{ __html: projectData?.goal }}
                                            />
                                        </Fragment>
                                    }
                                />
                                <Box display="flex" gap={1} mt={1}>
                                    {projectData?.categories.map((a, index) => (
                                        <Box key={index} sx={{ background: '#EFF6FF', p: '2px 14px', borderRadius: 1 }}>
                                            <Typography color="secondary.main" fontSize={14}>{a}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    </Fragment>
                }
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label='Reject'
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.background.main}
                            fontWeight={700}
                            marginBottom="10px"
                            handleClick={async () => {
                                try {
                                    dispatch(setLoading({
                                        loading: true
                                    }));
                                    const response = await rejectRequest({
                                        token: searchParams.get('token')
                                    });
                                    if (response.success) {
                                        showNotification(response?.message, 'success');
                                        setDialogOpen(false);
                                        dispatch(setLoading({
                                            loading: false
                                        }));
                                        navigate('/build-project');
                                    }
                                } catch (err) {
                                    setDialogOpen(false);
                                    const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                    showNotification(errorMessage, 'error');
                                    dispatch(setLoading({
                                        loading: false
                                    }));
                                }
                            }}
                        />
                        <CommonButton
                            label='Accept'
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.background.main}
                            fontWeight={700}
                            marginBottom="10px"
                            handleClick={async () => {
                                try {
                                    dispatch(setLoading({
                                        loading: true
                                    }));
                                    const response = await acceptRequest({
                                        token: searchParams.get('token')
                                    });
                                    if (response.success) {
                                        showNotification(response?.message, 'success');
                                        setDialogOpen(false);
                                        dispatch(setLoading({
                                            loading: false
                                        }));
                                        navigate('/build-project');
                                    }
                                } catch (err) {
                                    setDialogOpen(false);
                                    const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                    showNotification(errorMessage, 'error');
                                    dispatch(setLoading({
                                        loading: false
                                    }));
                                }
                            }}
                        />
                    </Fragment>
                )}
            />
            <CommonDialog
                open={addDialog}
                size="xs"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Add External Project</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setAddDialog(false);
                                    setFormData({
                                        title: '',
                                        category: [],
                                        status: Status[0].value,
                                        startdate: '',
                                        projectUrl: '',
                                        description: ''
                                    });
                                }}
                            />
                        </Box>
                        <Box width="80%" mb={1}>
                            <Typography sx={{ fontSize: 12 }} color={theme.palette.background.dark}>Document your personal research, internships, or side projects.</Typography>
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={1} mt={0.1}>
                        <Grid item xs={12}>
                            <CommonFormField
                                label='Project Title *'
                                type="email"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                error={errors.title}
                                placeholder='e.g. Personal Blog Design'
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CommonSelect
                                label="Domain/Category"
                                name="status"
                                multiple
                                value={formData?.category || []}
                                onChange={(e) => {
                                    let existingCategory = formData.category || [];
                                    // Find if this services already exists
                                    const existingIndex = existingCategory.findIndex(
                                        item => item === e.target.value
                                    );

                                    let updateCategory;

                                    if (existingIndex > - 1) {
                                        updateCategory.splice(existingIndex, 1);
                                    } else {
                                        existingCategory = e.target.value;
                                    }

                                    setFormData((prev) => ({
                                        ...prev,
                                        category: existingCategory
                                    }));
                                }}
                                placeholder="Select Category"
                                error={errors.category}
                                options={ProjectCategory}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CommonSelect
                                label="Project Status"
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                                placeholder="Completed / In Progress"
                                error={errors.status}
                                options={Status}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <CommonFormField
                                label="Start Date *"
                                type="date"
                                name="startdate"
                                value={formData.startdate}
                                onChange={handleInputChange}
                                error={errors.startdate}
                                placeholder="Select start date"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <CommonFormField
                                label='Project URL *'
                                type="email"
                                name="projectUrl"
                                value={formData.projectUrl}
                                onChange={handleInputChange}
                                error={errors.projectUrl}
                                placeholder='e.g. Link to Github'
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CommonFormField
                                label='Description'
                                type="email"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder='Briefly describe the problem you solved and the tools you used...'
                                multiline
                            />
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <Box display="flex" justifyContent="space-between" width="100%" mt={2} gap={1}>
                        <CommonButton
                            label="Cancel"
                            size="small"
                            background="white"
                            border={`1px solid #0000001A`}
                            color={theme.palette.background.dark}
                            fontWeight={700}
                            marginBottom="10px"
                            handleClick={() => {
                                setAddDialog(false);
                                setFormData({
                                    title: '',
                                    category: [],
                                    status: Status[0].value,
                                    startdate: '',
                                    projectUrl: '',
                                    description: ''
                                });
                            }}
                        />
                        <CommonButton
                            label="Save Project"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.background.main}
                            fontWeight={700}
                            marginBottom="10px"
                            handleClick={() => {
                                handleSubmit();
                            }}
                        />
                    </Box>
                )}
            />
            <CommonDialog
                open={successOpen}
                size="xs"
                title={
                    <Fragment>
                        <Close
                            sx={{
                                color: theme.palette.background.dark, fontSize: 20,
                                float: 'right',
                                cursor: 'pointer'
                            }}
                            onClick={() => {
                                setSuccess(false);
                                setAddDialog(true);
                            }}
                        />
                    </Fragment>
                }
                content={(
                    <center>
                        <img
                            src='/Images/success.png'
                            alt='Check Circle'
                            style={{ width: '70px', height: '70px', marginTop: 20 }}
                        />
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5 }}>
                            Project Added Successfully
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph mt={0.3}>
                            Project added to your portfolio successfully!
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <CommonButton
                        label="Return To Project"
                        size="small"
                        marginBottom="10px"
                        handleClick={() => {
                            setSuccess(false);
                        }}
                    />
                )}
            />
            <PageHeader
                title="Project Dashboard"
                content={
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography color="text.secondary" sx={{ fontSize: 14, }}>
                            My DCP Services
                        </Typography>
                        <FiberManualRecord sx={{ fontSize: 8, color: 'text.secondary' }} />
                        <Typography color="text.secondary" sx={{ fontSize: 14, }}>
                            Project Dashboard
                        </Typography>
                    </Stack>
                }
                buttonContent={
                    user?.user_type_id === UserTypeId.Student &&
                        (<CommonButton
                            marginBottom="0px"
                            label="Add External Project"
                            startIcon={<Add />}
                            width="200px"
                            handleClick={() => setAddDialog(true)}
                        />)
                }
            />
            <Grid container spacing={2} mb={2}>
                <Grid item sm={12}>
                    <Box mt={2} display="flex" gap={1}>
                        <CommonButton
                            label={ProjecType.DCP}
                            width="140px"
                            fontWeight={600}
                            color={projectType === ProjecType.DCP ? theme.palette.secondary.main : theme.palette.background.dark}
                            background={projectType === ProjecType.DCP ? theme.palette.primary.main : 'white'}
                            handleClick={() => setProjectType(ProjecType.DCP)}
                            marginBottom="0px"
                        />
                        <CommonButton
                            label={ProjecType.EXTERNAL}
                            width="160px"
                            fontWeight={600}
                            color={projectType === ProjecType.EXTERNAL ? theme.palette.secondary.main : theme.palette.background.dark}
                            background={projectType === ProjecType.EXTERNAL ? theme.palette.primary.main : 'white'}
                            handleClick={() => setProjectType(ProjecType.EXTERNAL)}
                            marginBottom="0px"
                        />
                        <CommonButton
                            label={ProjecType.EXPLORE}
                            width="170px"
                            fontWeight={600}
                            color={projectType === ProjecType.EXPLORE ? theme.palette.secondary.main : theme.palette.background.dark}
                            background={projectType === ProjecType.EXPLORE ? theme.palette.primary.main : 'white'}
                            handleClick={() => setProjectType(ProjecType.EXPLORE)}
                            marginBottom="0px"
                        />
                    </Box>
                    {projectType === ProjecType.DCP && (
                        <DcpProject setProjectType={setProjectType} projectList={projectList} />
                    )}
                    {projectType === ProjecType.EXTERNAL && (
                        <ExternalProject setProjectType={setProjectType} projectList={projectList} />
                    )}
                    {projectType === ProjecType.EXPLORE && (
                        <ExploreProject setType={setProjectType} />
                    )}
                </Grid>
            </Grid>
        </Fragment>
    )
}

export default BuildProject;